import { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage, LANGUAGES } from '@/context/LanguageContext';

// Compact selector for header
export function LanguageSelector({ className = '', variant = 'default' }) {
  const { language, setLanguage, currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (variant === 'icon') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-1"
          title="Select Language"
          data-testid="language-selector-icon"
        >
          <Globe className="w-5 h-5 text-slate-400" />
          <span className="text-xs font-medium text-slate-400 uppercase">{language}</span>
        </button>
        
        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-48 bg-slate-800 border border-white/10 rounded-xl shadow-xl z-50 py-2 max-h-80 overflow-y-auto">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-white/5 transition-colors ${
                  language === lang.code ? 'bg-blue-500/10 text-blue-400' : 'text-slate-300'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1">{lang.native}</span>
                {language === lang.code && <Check className="w-4 h-4 text-blue-400" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 border border-white/10 hover:border-white/20 transition-colors"
        data-testid="language-selector"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="text-sm text-slate-300">{currentLanguage.native}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-56 bg-slate-800 border border-white/10 rounded-xl shadow-xl z-50 py-2 max-h-80 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-white/5 transition-colors ${
                language === lang.code ? 'bg-blue-500/10' : ''
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <div className="flex-1">
                <p className={`text-sm ${language === lang.code ? 'text-blue-400' : 'text-slate-300'}`}>
                  {lang.native}
                </p>
                <p className="text-xs text-slate-500">{lang.name}</p>
              </div>
              {language === lang.code && <Check className="w-4 h-4 text-blue-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Footer language selector (minimal)
export function LanguageSelectorFooter({ className = '' }) {
  const { language, setLanguage, currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        data-testid="language-selector-footer"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLanguage.flag} {currentLanguage.native}</span>
      </button>
      
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-48 bg-slate-800 border border-white/10 rounded-xl shadow-xl z-50 py-2 max-h-64 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-white/5 transition-colors text-sm ${
                language === lang.code ? 'text-blue-400' : 'text-slate-300'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.native}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
